
import React, { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  ArrowUpDown, 
  Calendar, 
  Edit2, 
  MoreHorizontal, 
  Repeat, 
  Search, 
  Trash2, 
  ArrowDownCircle, 
  ArrowUpCircle 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { 
  Transacao, 
  TipoTransacao, 
  StatusTransacao 
} from "@/types/financeiro";

interface TransacoesListaProps {
  transacoes: Transacao[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

const TransacoesLista: React.FC<TransacoesListaProps> = ({ 
  transacoes, 
  onEdit, 
  onDelete 
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState<TipoTransacao | "todos">("todos");
  const [statusFiltro, setStatusFiltro] = useState<StatusTransacao | "todos">("todos");
  const [sortField, setSortField] = useState<keyof Transacao>("data");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const handleSort = (field: keyof Transacao) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Filtragem e ordenação
  const filteredTransacoes = transacoes.filter(transacao => {
    const matchesSearch = transacao.descricao.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transacao.categoria.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = tipoFiltro === "todos" || transacao.tipo === tipoFiltro;
    const matchesStatus = statusFiltro === "todos" || transacao.status === statusFiltro;
    
    return matchesSearch && matchesTipo && matchesStatus;
  }).sort((a, b) => {
    if (sortField === "valor") {
      return sortDirection === "asc" ? a.valor - b.valor : b.valor - a.valor;
    }
    
    if (sortField === "data") {
      return sortDirection === "asc" 
        ? new Date(a.data).getTime() - new Date(b.data).getTime() 
        : new Date(b.data).getTime() - new Date(a.data).getTime();
    }
    
    if (a[sortField] < b[sortField]) return sortDirection === "asc" ? -1 : 1;
    if (a[sortField] > b[sortField]) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const getStatusBadge = (status: StatusTransacao) => {
    switch (status) {
      case "pendente":
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">Pendente</Badge>;
      case "pago":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Pago</Badge>;
      case "atrasado":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Atrasado</Badge>;
      case "cancelado":
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-300">Cancelado</Badge>;
      default:
        return null;
    }
  };

  return (
    <Card className="w-full shadow-sm">
      <CardHeader className="bg-gray-50">
        <CardTitle className="text-xl">Lista de Transações</CardTitle>
        <div className="flex flex-col md:flex-row gap-4 mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar transações..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex space-x-2">
            <Button 
              variant={tipoFiltro === "todos" ? "default" : "outline"}
              onClick={() => setTipoFiltro("todos")}
              className="whitespace-nowrap"
            >
              Todos
            </Button>
            <Button 
              variant={tipoFiltro === "receita" ? "default" : "outline"}
              onClick={() => setTipoFiltro("receita")}
              className="text-green-600 whitespace-nowrap"
            >
              <ArrowUpCircle className="mr-1 h-4 w-4" />
              Receitas
            </Button>
            <Button 
              variant={tipoFiltro === "despesa" ? "default" : "outline"}
              onClick={() => setTipoFiltro("despesa")}
              className="text-red-600 whitespace-nowrap"
            >
              <ArrowDownCircle className="mr-1 h-4 w-4" />
              Despesas
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[120px]">
                  <div 
                    className="flex items-center cursor-pointer"
                    onClick={() => handleSort("data")}
                  >
                    <Calendar className="mr-1 h-4 w-4" />
                    Data
                    <ArrowUpDown className="ml-1 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Categoria</TableHead>
                <TableHead>
                  <div 
                    className="flex items-center cursor-pointer"
                    onClick={() => handleSort("valor")}
                  >
                    Valor
                    <ArrowUpDown className="ml-1 h-4 w-4" />
                  </div>
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTransacoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nenhuma transação encontrada
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransacoes.map((transacao) => (
                  <TableRow key={transacao.id}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{format(new Date(transacao.data), "dd/MM/yyyy")}</span>
                        {transacao.recorrente && (
                          <div className="flex items-center text-xs text-muted-foreground mt-1">
                            <Repeat className="h-3 w-3 mr-1" />
                            {transacao.periodicidade}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{transacao.descricao}</div>
                    </TableCell>
                    <TableCell>{transacao.categoria}</TableCell>
                    <TableCell className={transacao.tipo === "receita" ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                      {transacao.tipo === "receita" ? "+" : "-"}
                      {transacao.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(transacao.status)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Abrir menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onEdit(transacao.id)}>
                            <Edit2 className="mr-2 h-4 w-4" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onDelete(transacao.id)} className="text-red-600">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TransacoesLista;
